/**
 * Hidden call-limit settings (super admins only) — form ↔ wire helpers for
 * `GET/POST /admin/call-limits`.
 */

export interface CallLimitConfig {
  verificationMaxSec: number;
  nightEnabled: boolean;
  nightMinutes: number;
  nightStartMinute: number;
  nightEndMinute: number;
}

export interface NightLimitUser {
  id: string;
  handle: string;
  displayName: string | null;
  isVerificationAccount: boolean;
  nightCallLimited: boolean;
  adminCredited: boolean;
  limited: boolean;
  tonight: { windowOpen: boolean; usedSec: number; limitSec: number };
}

export const DEFAULT_CONFIG: CallLimitConfig = {
  verificationMaxSec: 120,
  nightEnabled: true,
  nightMinutes: 15,
  nightStartMinute: 22 * 60,
  nightEndMinute: 6 * 60,
};

/** 1320 → "22:00" (the value a native time input takes). */
export function minuteToTime(m: number): string {
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** "22:00" → 1320; null for anything else. */
export function timeToMinute(t: string): number | null {
  const m = /^(\d{2}):(\d{2})$/.exec(t);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h < 24 && min < 60 ? h * 60 + min : null;
}

/** "2 min", "2 min 30 s", "45 s". */
export function formatSeconds(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m === 0) return `${s} s`;
  return s === 0 ? `${m} min` : `${m} min ${s} s`;
}

export function configError(c: CallLimitConfig): string | null {
  if (!Number.isInteger(c.verificationMaxSec) || c.verificationMaxSec < 30 || c.verificationMaxSec > 3600) {
    return 'Verification call length must be between 30 seconds and 60 minutes.';
  }
  if (!Number.isInteger(c.nightMinutes) || c.nightMinutes < 1 || c.nightMinutes > 600) {
    return 'Night minutes must be between 1 and 600.';
  }
  if (c.nightStartMinute === c.nightEndMinute) return 'The night window must start and end at different times.';
  return null;
}
